import { Center, Text, HStack, Box, BoxProps, Flex } from '@chakra-ui/react'
import {
  ProductCustomOptions,
  ProductOption,
  ProductOptionKey,
} from '_app/types/product'

import { getEntries } from '_app/utils/getEntries'
import {
  BundleCartItem,
  EnteredOptionsCheckOutput,
} from '_app/graphql/generated/graphql'
import useProductOptions, { OptionType } from '_app/hooks/useProductOptions'
import { ImageCms } from '_app/components'
import CartOptionHeader from './CartOptionHeader'
import { useTranslation } from 'next-i18next'

interface ProductOptionsProps extends Omit<BoxProps, 'onChange'> {
  cartItem: BundleCartItem;
  options: ProductCustomOptions;
  requiredOptions?: {
    type: OptionType;
    options: { label: string; value: string }[];
  }[];
  currentUrlKey?: any;
}

const getOptionValue = ({
  options,
  currentUrlKey,
  option,
}: {
  options: ProductCustomOptions;
  currentUrlKey?: string | string[];
  option: ProductOptionKey;
}): string =>
  options?.[option]?.find(({ url_key }) => url_key === currentUrlKey)?.value ??
  ''

const getImageSize = (key: ProductOptionKey): number => {
  switch (key) {
    case 'alfabet_swatch':
    case 'cyfry_swatch':
    case 'znaki_zodiaku_swatch':
      return 16
    default:
      return 5
  }
}

const CartProductOptions = ({
  cartItem,
  options,
  currentUrlKey,
  requiredOptions,
  ...props
}: ProductOptionsProps) => {
  const { getHeader } = useProductOptions({ product: undefined })
  const { t } = useTranslation('product')

  const header: { [key in ProductOptionKey]: string } = {
    alfabet_swatch: t('ELEMENTS'),
    kolor3_swatch: t('COLOR'),
    cyfry_swatch: t('ELEMENTS'),
    kolor_emalii_swatch: t('COLOR'),
    kolor_kamienia_swatch: t('COLOR'),
    material_swatch: t('TYPE_OF_FINISH', { ns: 'common' }),
    rodzaj_kamienia_swatch: t('TYPE_OF_FINISH', { ns: 'common' }),
    rozmiar_swatch: t('SIZE', { ns: 'common' }),
    znaki_zodiaku_swatch: t('ELEMENTS'),
  }

  const sizeOptions = !cartItem?.bundle_options?.some(
    opt => opt?.label?.toLowerCase() === 'rozmiar'
  )
  const sizeEnteredOptions = cartItem?.entered_options?.option?.filter(
    (opt): opt is EnteredOptionsCheckOutput =>
      !!opt?.label?.toLowerCase().includes('rozmiar')
  )

  const swatchOptions = getEntries<ProductOptionKey, ProductOption[]>(
    options
  )?.filter(([_key, value]) => value.length > 1)

  const areOptionsAvailable =
    (sizeOptions && sizeEnteredOptions?.length) ||
    swatchOptions.length > 0 ||
    requiredOptions?.length

  if (!areOptionsAvailable) {
    return null
  }

  return (
    <Box
      py={3}
      {...props}
      sx={{
        '&>div:not(:last-child)': {
          paddingBottom: 4,
          marginBottom: 4,
          borderBottom: 'solid 1px',
          borderColor: 'borderLight',
        },
      }}
    >
      {sizeOptions &&
        sizeEnteredOptions?.map((opt, idx) => (
          <Box key={opt.label || idx}>
            <CartOptionHeader
              option={getHeader('size')}
              value={opt.value || undefined}
            />
          </Box>
        ))}
      {requiredOptions?.map((option, index) => (
        <Box key={option.options[0]?.label || `option-${index}`}>
          <CartOptionHeader
            option={getHeader(option?.type)}
            value={
              option.options.find(
                ({ value }) =>
                  value === cartItem.bundle_options?.[index]?.values?.[0]?.uid
              )?.label
            }
          />
        </Box>
      ))}
      {swatchOptions?.map(([key, value]) => (
        <Flex key={key} alignItems="center">
          {key !== 'rozmiar_swatch' && (
            <CartOptionHeader
              option={header[key]}
              value={getOptionValue({
                option: key,
                options,
                currentUrlKey,
              })}
            />
          )}
          <HStack flexWrap="wrap">
            {value?.map(({ url_key, value, image }, index) => {
              if (url_key !== currentUrlKey) return null
              const chakraSize = getImageSize(key)
              const size = `${chakraSize * 4}`
              return (
                <Box key={index}>
                  {image && key !== 'rozmiar_swatch' ? (
                    <Box p={2} boxSize={chakraSize} boxSizing="content-box">
                      <ImageCms
                        src={''}
                        width={Number(size)}
                        height={Number(size)}
                        attributes={{ url: image }}
                        alt={url_key || ''}
                      />
                    </Box>
                  ) : (
                    <Center px={2} py={1}>
                      <Text>{value}</Text>
                    </Center>
                  )}
                </Box>
              )
            })}
          </HStack>
        </Flex>
      ))}
    </Box>
  )
}

export default CartProductOptions
